/**
 * Contact Service
 * 
 * Handles contact form submissions and management
 */

import { safeFrom, safeAuth, isSupabaseAvailable, getSupabase } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import { 
  sendContactFormNotification,
  sendContactFormAutoReply
} from './emailService';
import type { 
  ContactFormData, 
  ContactSubmission, 
  ContactSubmissionStatus,
  GetSubmissionsOptions 
} from '@/types/contact';

/**
 * Submit contact form
 */
export const submitContactForm = async (
  data: ContactFormData
): Promise<ServiceResponse<ContactSubmission>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Validate required fields
    if (!data.name || !data.email || !data.subject || !data.message) {
      return errorResponse(handleServiceError(
        new Error('All required fields must be filled'),
        'contactService.submitContactForm',
        { data }
      ));
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return errorResponse(handleServiceError(
        new Error('Invalid email address'),
        'contactService.submitContactForm',
        { data }
      ));
    }

    // Insert via SECURITY DEFINER RPC so anonymous visitors can submit
    // AND receive the created row back without needing SELECT on the
    // contact_submissions table (which is admin-only by RLS).
    const client = getSupabase();
    const { data: submission, error: insertError } = await client.rpc('submit_contact_form', {
      p_name: data.name,
      p_email: data.email,
      p_subject: data.subject,
      p_message: data.message,
      p_phone: data.phone ?? null,
    });

    if (insertError) {
      throw insertError;
    }

    if (!submission) {
      throw new Error('Contact submission failed: no row returned');
    }

    const created = submission as ContactSubmission;

    // Send notification email to admin (non-blocking)
    // Get admin email from site config
    const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'admin@example.com';
    sendContactFormNotification({
      name: created.name,
      email: created.email,
      subject: created.subject,
      message: created.message,
      phone: created.phone || undefined,
    }, adminEmail).catch(error => {
      console.error('[contactService] Failed to send notification email:', error);
    });

    // Send auto-reply to user (non-blocking)
    // We'll need to get organization name from config
    // For now, use a default
    const organizationName = import.meta.env.VITE_ORG_NAME || 'Our Organization';
    sendContactFormAutoReply({
      name: created.name,
      email: created.email,
    }, organizationName).catch(error => {
      console.error('[contactService] Failed to send auto-reply email:', error);
    });

    // Public contact-form inserts are made by anonymous visitors, who
    // don't have INSERT rights on audit_logs. The contact_submissions
    // row itself is the record of the event, so we intentionally skip
    // audit logging here to avoid noisy 42501 errors in the console.

    return successResponse(created);
  } catch (error) {
    console.error('[contactService] Error submitting contact form:', error);
    return errorResponse(handleServiceError(error, 'contactService.submitContactForm', { data }));
  }
};

/**
 * Get contact submissions (admin only)
 */
export const getContactSubmissions = async (
  options: GetSubmissionsOptions = {}
): Promise<ServiceResponse<ContactSubmission[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Verify admin access
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    // Check if user is admin (this would be done via RLS in production)
    // For now, we'll rely on RLS policies

    let query = safeFrom('contact_submissions')
      .select('*');

    // Apply filters
    if (options.status) {
      query = query.eq('status', options.status) as any;
    }

    // Apply ordering
    const orderBy = options.orderBy || 'created_at';
    const orderDirection = options.orderDirection || 'desc';
    query = query.order(orderBy, { ascending: orderDirection === 'asc' }) as any;

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit) as any;
    }
    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1) as any;
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return successResponse((data || []) as ContactSubmission[]);
  } catch (error) {
    console.error('[contactService] Error fetching contact submissions:', error);
    return errorResponse(handleServiceError(error, 'contactService.getContactSubmissions', { options }));
  }
};

/**
 * Update submission status (admin only)
 */
export const updateSubmissionStatus = async (
  id: number,
  status: ContactSubmissionStatus
): Promise<ServiceResponse<ContactSubmission>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: submission, error: updateError } = await safeFrom('contact_submissions')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    const updated = submission as ContactSubmission;

    // Audit log
    await auditContent.update('contact_submission', id.toString(), {}, {
      status: updated.status,
    });

    return successResponse(updated);
  } catch (error) {
    console.error('[contactService] Error updating submission status:', error);
    return errorResponse(handleServiceError(error, 'contactService.updateSubmissionStatus', { id, status }));
  }
};

/**
 * Get submission by ID (admin only)
 */
export const getSubmissionById = async (
  id: number
): Promise<ServiceResponse<ContactSubmission | null>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('contact_submissions')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return successResponse(data as ContactSubmission | null);
  } catch (error) {
    console.error('[contactService] Error fetching submission:', error);
    return errorResponse(handleServiceError(error, 'contactService.getSubmissionById', { id }));
  }
};

export const contactService = {
  submitContactForm,
  getContactSubmissions,
  updateSubmissionStatus,
  getSubmissionById,
};

